import { useEffect, useRef, useState } from 'react';

import { downloadCandidateVideo } from '@/modules/hrPortal/api/candidates.api';

interface UseCandidateVideoOptions {
  applicationId: string;
  hasVideo: boolean;
  onError?: (message: string) => void;
}

export default function useCandidateVideo({
  applicationId,
  hasVideo,
  onError,
}: UseCandidateVideoOptions) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    return () => {
      if (videoUrl) {
        window.URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const playVideo = async () => {
    if (!applicationId || !hasVideo) {
      onError?.('Video is not available.');
      return;
    }

    if (videoUrl && videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      } else {
        await videoRef.current.play();
        setIsVideoPlaying(true);
      }
      return;
    }

    try {
      const response = await downloadCandidateVideo(applicationId);
      const contentType = response.headers?.['content-type'];
      const blob = new Blob([response.data], {
        type: typeof contentType === 'string' ? contentType : 'video/mp4',
      });
      const url = window.URL.createObjectURL(blob);
      setVideoUrl(url);
      setIsVideoPlaying(true);
    } catch (error) {
      console.error('Video play failed:', error);
      onError?.('Unable to load candidate video.');
    }
  };

  const toggleMute = () => {
    setIsMuted((value) => {
      const next = !value;
      if (videoRef.current) {
        videoRef.current.muted = next;
      }
      return next;
    });
  };

  const toggleFullscreen = async () => {
    if (!videoRef.current) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await videoRef.current.requestFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen failed:', error);
    }
  };

  return {
    videoRef,
    videoUrl,
    isVideoPlaying,
    isMuted,
    setIsVideoPlaying,
    playVideo,
    toggleMute,
    toggleFullscreen,
  };
}
